// P12 — Fila admin de pedidos de aumento de cota.
//
// Junta os pedidos das DUAS tabelas de cota: o Hub de Agentes
// (`/agents/quota/requests/`) e o Assistente (`/assistant/quota/requests/`).
// Cada linha mostra a origem; approve/reject são roteados pro endpoint certo.
// Aprovar libera os limites na hora; rejeitar manda uma notificação ao user.

import { FC, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import adminAgentsService from '@/services/adminAgents.service';
import assistantQuotaService from '@/services/assistantQuota.service';
import { useToast } from '@/contexts/ToastContext';
import { AgentQuotaIncreaseRequest } from '@/types/agents';
import { GeneralQuotaIncreaseRequest } from '@/types/assistant';

type QuotaSource = 'hub' | 'assistant';

// Linha unificada: a forma é idêntica entre Hub e Assistente; só o conjunto de
// `requested_field` difere — por isso `requested_field` vira string genérica.
type QuotaRequestRow = Omit<
  AgentQuotaIncreaseRequest & GeneralQuotaIncreaseRequest,
  'requested_field'
> & {
  requested_field: string;
  source: QuotaSource;
};

// Cobre os campos das duas tabelas (Hub + Assistente). Fallback pro nome cru.
const FIELD_LABELS: Record<string, string> = {
  // Hub de Agentes
  max_personal_agents: 'Agentes pessoais (qtd)',
  max_files_per_agent: 'Arquivos por agente',
  max_file_size_mb: 'Tamanho de arquivo (MB)',
  // comuns
  max_tokens_per_day: 'Tokens / dia',
  max_tokens_per_month: 'Tokens / mês',
  // Assistente
  max_files_per_day: 'Arquivos gerados / dia',
  max_uploads_per_day: 'Uploads / dia',
  max_web_searches_per_day: 'Buscas na web / dia',
};

const SOURCE_LABELS: Record<QuotaSource, string> = {
  hub: 'Hub',
  assistant: 'Assistente',
};

function fieldLabel(field: string): string {
  return FIELD_LABELS[field] || field;
}

function fmtDate(s: string): string {
  return new Date(s).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

// suggested_value e a cota podem vir nulos (pedido sem sugestão de valor) — nunca
// chamar .toLocaleString direto num número possivelmente nulo.
function fmtNum(value: number | null | undefined): string {
  return typeof value === 'number' ? value.toLocaleString('pt-BR') : '—';
}

interface ApproveModalProps {
  request: QuotaRequestRow;
  onClose: () => void;
  onSubmit: (newValue: number, notes: string) => Promise<void>;
}

const ApproveModal: FC<ApproveModalProps> = ({ request, onClose, onSubmit }) => {
  const [newValue, setNewValue] = useState<string>(
    request.suggested_value != null ? String(request.suggested_value) : '',
  );
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [currentValue, setCurrentValue] = useState<number | null>(null);
  const [loadingCurrent, setLoadingCurrent] = useState(true);

  // Busca a cota ATUAL do user pro campo pedido, pra o admin ter uma base.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const quota =
          request.source === 'assistant'
            ? await assistantQuotaService.getUserQuota(request.user)
            : await adminAgentsService.getUserQuota(request.user);
        const value = (quota as unknown as Record<string, unknown>)[request.requested_field];
        if (alive) setCurrentValue(typeof value === 'number' ? value : null);
      } catch {
        if (alive) setCurrentValue(null);
      } finally {
        if (alive) setLoadingCurrent(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [request.user, request.source, request.requested_field]);

  const handleSubmit = async () => {
    const n = Number(newValue);
    if (!Number.isFinite(n) || n <= 0) return;
    setBusy(true);
    try {
      await onSubmit(n, notes);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
      data-testid="approve-modal"
    >
      <div
        className="bg-surface-primary border border-surface-border rounded-xl max-w-md w-full p-6 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-bold text-content-primary">Aprovar aumento de cota</h2>
        <div className="text-sm space-y-1">
          <p>
            <span className="text-content-tertiary">Usuário: </span>
            <strong className="text-content-primary">{request.user_name}</strong>
          </p>
          <p>
            <span className="text-content-tertiary">Origem: </span>
            <strong className="text-content-primary">{SOURCE_LABELS[request.source]}</strong>
          </p>
          <p>
            <span className="text-content-tertiary">Campo: </span>
            <strong className="text-content-primary">{fieldLabel(request.requested_field)}</strong>
          </p>
          <p>
            <span className="text-content-tertiary">Valor atual: </span>
            <strong className="text-content-primary" data-testid="approve-current-value">
              {loadingCurrent ? '…' : fmtNum(currentValue)}
            </strong>
          </p>
          <p>
            <span className="text-content-tertiary">Valor sugerido pelo user: </span>
            <strong className="text-content-primary">{fmtNum(request.suggested_value)}</strong>
          </p>
          <div className="pt-1">
            <span className="text-content-tertiary block mb-1">Motivo do pedido:</span>
            <p
              className="text-content-primary whitespace-pre-wrap break-words rounded-lg bg-surface-secondary border border-surface-border p-2 max-h-40 overflow-y-auto"
              data-testid="approve-reason"
            >
              {request.reason || '—'}
            </p>
          </div>
        </div>

        <label className="block">
          <span className="text-sm font-semibold text-content-primary block mb-1">Novo valor</span>
          <input
            type="number"
            min={1}
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            data-testid="approve-new-value"
            className="w-full rounded-lg bg-surface-secondary border border-surface-border px-3 py-2 text-content-primary"
          />
          <p className="text-xs text-content-tertiary mt-1">
            {currentValue !== null
              ? `Atual: ${fmtNum(currentValue)} · você pode aprovar com qualquer valor.`
              : 'Você pode aprovar com um valor diferente do sugerido.'}
          </p>
        </label>

        <label className="block">
          <span className="text-sm font-semibold text-content-primary block mb-1">Notas (opcional)</span>
          <textarea
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            data-testid="approve-notes"
            className="w-full resize-none rounded-lg bg-surface-secondary border border-surface-border px-3 py-2 text-content-primary text-sm"
          />
        </label>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-lg border border-surface-border px-4 py-2 text-sm text-content-primary"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={busy}
            data-testid="approve-confirm"
            className="rounded-lg bg-green-500 hover:bg-green-400 text-white px-4 py-2 text-sm font-semibold disabled:opacity-50"
          >
            Confirmar aprovação
          </button>
        </div>
      </div>
    </div>
  );
};

interface RejectModalProps {
  request: QuotaRequestRow;
  onClose: () => void;
  onSubmit: (notes: string) => Promise<void>;
}

const RejectModal: FC<RejectModalProps> = ({ request, onClose, onSubmit }) => {
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const handleSubmit = async () => {
    setBusy(true);
    try {
      await onSubmit(notes);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
      data-testid="reject-modal"
    >
      <div
        className="bg-surface-primary border border-surface-border rounded-xl max-w-md w-full p-6 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-bold text-content-primary">Rejeitar pedido</h2>
        <p className="text-sm text-content-tertiary">
          O user vai receber uma notificação. Você pode explicar o motivo.
        </p>
        <p className="text-sm">
          <span className="text-content-tertiary">Usuário: </span>
          <strong className="text-content-primary">{request.user_name}</strong>
        </p>
        <div className="text-sm">
          <span className="text-content-tertiary block mb-1">Motivo do pedido:</span>
          <p
            className="text-content-primary whitespace-pre-wrap break-words rounded-lg bg-surface-secondary border border-surface-border p-2 max-h-40 overflow-y-auto"
            data-testid="reject-reason"
          >
            {request.reason || '—'}
          </p>
        </div>
        <label className="block">
          <span className="text-sm font-semibold text-content-primary block mb-1">Notas (opcional)</span>
          <textarea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            data-testid="reject-notes"
            className="w-full resize-none rounded-lg bg-surface-secondary border border-surface-border px-3 py-2 text-content-primary text-sm"
          />
        </label>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-lg border border-surface-border px-4 py-2 text-sm text-content-primary"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={busy}
            data-testid="reject-confirm"
            className="rounded-lg bg-red-500 hover:bg-red-400 text-white px-4 py-2 text-sm font-semibold disabled:opacity-50"
          >
            Rejeitar
          </button>
        </div>
      </div>
    </div>
  );
};

const AdminAgentQuotaRequestsPage: FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [requests, setRequests] = useState<QuotaRequestRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState<QuotaRequestRow | null>(null);
  const [rejecting, setRejecting] = useState<QuotaRequestRow | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    // Best-effort por origem: se uma fonte falhar, a outra ainda aparece.
    const [hub, assistant] = await Promise.all([
      adminAgentsService.listQuotaIncreaseRequests({ status: 'PENDING' }).catch(() => {
        showToast('Não foi possível carregar os pedidos do Hub.', 'error');
        return [] as AgentQuotaIncreaseRequest[];
      }),
      assistantQuotaService.listIncreaseRequests({ status: 'PENDING' }).catch(() => {
        showToast('Não foi possível carregar os pedidos do Assistente.', 'error');
        return [] as GeneralQuotaIncreaseRequest[];
      }),
    ]);
    const rows: QuotaRequestRow[] = [
      ...hub.map((r) => ({ ...r, source: 'hub' as const })),
      ...assistant.map((r) => ({ ...r, source: 'assistant' as const })),
    ].sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
    setRequests(rows);
    setLoading(false);
  }, [showToast]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleApprove = async (newValue: number, notes: string) => {
    if (!approving) return;
    try {
      if (approving.source === 'assistant') {
        await assistantQuotaService.approveIncreaseRequest(approving.id, newValue, notes);
      } else {
        await adminAgentsService.approveQuotaIncrease(approving.id, newValue, notes);
      }
      showToast('Cota aumentada.', 'success');
      setApproving(null);
      void refresh();
    } catch {
      showToast('Erro ao aprovar.', 'error');
    }
  };

  const handleReject = async (notes: string) => {
    if (!rejecting) return;
    try {
      if (rejecting.source === 'assistant') {
        await assistantQuotaService.rejectIncreaseRequest(rejecting.id, notes);
      } else {
        await adminAgentsService.rejectQuotaIncrease(rejecting.id, notes);
      }
      showToast('Pedido rejeitado.', 'success');
      setRejecting(null);
      void refresh();
    } catch {
      showToast('Erro ao rejeitar.', 'error');
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="admin-quota-requests-page">
      <header className="mb-6">
        <button
          type="button"
          onClick={() => navigate('/admin/agentes')}
          className="text-sm text-content-tertiary hover:text-content-primary mb-2"
        >
          ← Voltar pra agentes
        </button>
        <h1 className="text-2xl font-bold text-content-primary">
          Pedidos de aumento de cota
          <span className="ml-2 text-base font-normal text-content-tertiary">
            ({requests.length})
          </span>
        </h1>
        <p className="text-sm text-content-tertiary mt-1">
          Inclui pedidos do Hub de Agentes e do Assistente. Aprovar libera os limites na hora;
          rejeitar manda uma notificação ao user.
        </p>
      </header>

      {loading ? (
        <p className="text-sm text-content-tertiary">Carregando…</p>
      ) : requests.length === 0 ? (
        <p
          data-testid="empty-list"
          className="text-sm text-content-tertiary rounded-lg border border-dashed border-surface-border p-12 text-center"
        >
          Nenhum pedido pendente.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-surface-border">
          <table className="w-full text-sm">
            <thead className="bg-surface-secondary">
              <tr>
                <th className="text-left px-3 py-2 text-content-tertiary font-medium">Origem</th>
                <th className="text-left px-3 py-2 text-content-tertiary font-medium">Usuário</th>
                <th className="text-left px-3 py-2 text-content-tertiary font-medium">Campo</th>
                <th className="text-right px-3 py-2 text-content-tertiary font-medium">Sugerido</th>
                <th className="text-left px-3 py-2 text-content-tertiary font-medium">Motivo</th>
                <th className="text-left px-3 py-2 text-content-tertiary font-medium">Quando</th>
                <th className="text-right px-3 py-2 text-content-tertiary font-medium">Ações</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((r) => (
                <tr
                  key={`${r.source}-${r.id}`}
                  className="border-t border-surface-border"
                  data-testid={`quota-row-${r.id}`}
                >
                  <td className="px-3 py-2">
                    <span
                      data-testid={`source-${r.id}`}
                      className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${
                        r.source === 'assistant'
                          ? 'bg-accent/15 text-accent'
                          : 'bg-surface-secondary text-content-tertiary'
                      }`}
                    >
                      {SOURCE_LABELS[r.source]}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-content-primary">{r.user_name}</td>
                  <td className="px-3 py-2 text-content-primary">{fieldLabel(r.requested_field)}</td>
                  <td className="px-3 py-2 text-right text-content-primary">{fmtNum(r.suggested_value)}</td>
                  <td className="px-3 py-2 text-content-tertiary max-w-sm truncate" title={r.reason}>
                    {r.reason}
                  </td>
                  <td className="px-3 py-2 text-content-tertiary text-xs">{fmtDate(r.created_at)}</td>
                  <td className="px-3 py-2 text-right space-x-2 whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => setRejecting(r)}
                      data-testid={`reject-${r.id}`}
                      className="rounded-lg border border-red-500/40 text-red-300 hover:bg-red-500/10 px-3 py-1 text-xs font-semibold"
                    >
                      Rejeitar
                    </button>
                    <button
                      type="button"
                      onClick={() => setApproving(r)}
                      data-testid={`approve-${r.id}`}
                      className="rounded-lg bg-green-500/90 hover:bg-green-500 text-white px-3 py-1 text-xs font-semibold"
                    >
                      Aprovar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {approving && (
        <ApproveModal
          request={approving}
          onClose={() => setApproving(null)}
          onSubmit={handleApprove}
        />
      )}
      {rejecting && (
        <RejectModal
          request={rejecting}
          onClose={() => setRejecting(null)}
          onSubmit={handleReject}
        />
      )}
    </div>
  );
};

export default AdminAgentQuotaRequestsPage;
